import { FC, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { types, Badge, KeyValueTable, utils } from "@vilocnv/allsetra-core";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import { RideHistoryModal } from "domain/rides/components/history";
import { useRideHistory } from "domain/rides/hooks/use-ride-history";
import UpdateObjectRideForm from "components/forms/objects/UpdateObjectRideForm/UpdateObjectRideForm";
import LocationTimeline from "components/ui/LocationTimeline/LocationTimeline";
import MixedRideStackedBar from "./children/MixedRideStackedBar";
import MixedRideTimeline from "./children/MixedRideTimeline";
import {
  SpeedIcon,
  Clock,
  LocationIcon,
  MileageIcon,
  SpeedIconRed,
  DetourIcon,
  PrivateMileageIcon
} from "assets/icons";
import {
  IconBox,
  RecordKey,
  RecordValue,
  RecordsWrapper,
  RideBox,
  RideMessage,
  RidesContainer
} from "./ObjectRideDetailsDrawer.styled";
import { Warning } from "@mui/icons-material";
// Data
import { isEmpty, round } from "lodash";
import {
  transformObjectRideLocationsForTimeline,
  OBJECT_RIDE_MODES,
  OBJECT_RIDE_MODES_COLOR,
  getObjectRideTotalTimeInMinutes,
  isManualRide,
  isOngoingRide,
  IObjectRideWithSegments
} from "app/data/helpers/objectRideHelpers";
import {
  getAddressDisplayInfo,
  getResolvedAddressFromStore
} from "app/data/helpers/addressValidationHelpers";
import {
  isMixedRideWithSegments,
  buildMixedRideTimelineStops,
  getMixedRideDisplaySegments,
  MixedRideTimelineStop
} from "app/data/helpers/mixedRideSegmentHelpers";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { useAppSelector } from "hooks";
import {
  selectDrawerSelectedAccountId,
  selectQueriedObjectsState
} from "app/data/selectors";

interface Props {
  showDrawer?: boolean;
  objectRide: types.IObjectRide | null;
  objectRideLocations: Array<types.IObjectRideLocation>;
  onLocationItemClick: (data: types.IObjectRideLocation) => void;
  resolvedAddresses?: any[];
  resolvedAddressLoading?: boolean;
  canViewAddressForRide?: (ride: types.IObjectRide) => boolean;
  selectedSegmentId?: string | null;
  onSegmentSelect?: (segmentId: string) => void;
}

const ObjectRideDetailsDrawer: FC<Props> = ({
  objectRide,
  objectRideLocations,
  onLocationItemClick,
  resolvedAddresses = [],
  resolvedAddressLoading = false,
  canViewAddressForRide,
  selectedSegmentId,
  onSegmentSelect
}) => {
  const navigate = useNavigate();

  const { t } = useTranslation([
    "translation",
    "tableHeadingsTranslation",
    "formFieldsTranslation"
  ]);

  const params = useParams();
  const drawerSelectedAccountId =
    useAppSelector(selectDrawerSelectedAccountId) || "";
  const { specificObject } = useAppSelector(selectQueriedObjectsState);
  const resolvedObjectId = params.objectId || specificObject?.uniqueId || "";

  const [openUpdateRide, setOpenUpdateRide] = useState<boolean>(false);
  const ongoing = isOngoingRide(objectRide);
  const hideSpeedAndDetails =
    isManualRide(objectRide) || isEmpty(objectRideLocations);
  const showMixedRideSegments = isMixedRideWithSegments(objectRide);
  const mixedRide = objectRide as IObjectRideWithSegments | null;
  const mixedRideSegments = useMemo(
    () => getMixedRideDisplaySegments(mixedRide),
    [mixedRide]
  );
  const hasMaxSpeed =
    typeof objectRide?.maxSpeed === "number" &&
    Number.isFinite(objectRide.maxSpeed);
  const hasAverageSpeed =
    typeof objectRide?.averageSpeed === "number" &&
    Number.isFinite(objectRide.averageSpeed);

  const rideHistory = useRideHistory({
    accountId: drawerSelectedAccountId,
    objectId: resolvedObjectId,
    rideId: params.rideId || objectRide?.uniqueId || ""
  });

  const timeLineItems = useMemo(() => {
    const userCanViewAddress =
      objectRide && canViewAddressForRide
        ? canViewAddressForRide(objectRide)
        : true;

    const isPrivateOrMixed =
      objectRide?.rideMode === 2 || objectRide?.rideMode === 4;
    const shouldForceHideAddress = !userCanViewAddress && isPrivateOrMixed;

    // Get resolved addresses for start and end locations
    const startResolvedAddress = getResolvedAddressFromStore(
      null,
      resolvedAddresses,
      objectRide?.startLatitude,
      objectRide?.startLongitude
    );

    const endResolvedAddress = ongoing
      ? undefined
      : getResolvedAddressFromStore(
          null,
          resolvedAddresses,
          objectRide?.endLatitude,
          objectRide?.endLongitude
        );

    // Get address display info based on coordinates
    const startAddressInfo = getAddressDisplayInfo(
      objectRide?.startLatitude,
      objectRide?.startLongitude,
      startResolvedAddress
    );

    const endAddressInfo = getAddressDisplayInfo(
      objectRide?.endLatitude,
      objectRide?.endLongitude,
      endResolvedAddress
    );

    // Determine what to display for start address
    let displayStartAddress = startResolvedAddress || "";
    if (shouldForceHideAddress) {
      displayStartAddress = t("typography.addressIsHidden");
    } else if (startAddressInfo.isHidden) {
      displayStartAddress = t("typography.addressIsHidden");
    } else if (startAddressInfo.isUnknown) {
      displayStartAddress = t("typography.addressUnknown");
    } else if (startAddressInfo.isNA) {
      displayStartAddress = "N/A";
    }

    // Determine what to display for end address
    let displayEndAddress = ongoing
      ? t("common.ongoingRide")
      : endResolvedAddress || "";
    if (!ongoing) {
      if (shouldForceHideAddress) {
        displayEndAddress = t("typography.addressIsHidden");
      } else if (endAddressInfo.isHidden) {
        displayEndAddress = t("typography.addressIsHidden");
      } else if (endAddressInfo.isUnknown) {
        displayEndAddress = t("typography.addressUnknown");
      } else if (endAddressInfo.isNA) {
        displayEndAddress = "N/A";
      }
    }

    const items = transformObjectRideLocationsForTimeline(
      objectRideLocations,
      objectRide
    );

    const updatedItems = items.map((item, index) => ({
      ...item,
      startAddress: shouldForceHideAddress
        ? displayStartAddress
        : displayStartAddress || item.startAddress,
      endAddress: shouldForceHideAddress
        ? displayEndAddress
        : displayEndAddress || item.endAddress
    }));

    return updatedItems.length > 2
      ? [updatedItems[0], updatedItems[updatedItems.length - 1]]
      : updatedItems;
  }, [
    objectRideLocations,
    objectRide,
    resolvedAddresses,
    t,
    canViewAddressForRide,
    ongoing
  ]);

  const mixedRideStops = useMemo((): MixedRideTimelineStop[] => {
    if (!showMixedRideSegments || !mixedRide || !mixedRideSegments.length) {
      return [];
    }

    const resolveStopAddress = (stop: MixedRideTimelineStop) => {
      const resolvedAddress = getResolvedAddressFromStore(
        stop.address,
        resolvedAddresses,
        stop.latitude,
        stop.longitude
      );
      const addressInfo = getAddressDisplayInfo(
        stop.latitude,
        stop.longitude,
        resolvedAddress
      );

      if (addressInfo.isHidden) {
        return t("typography.addressIsHidden");
      }
      if (addressInfo.isUnknown) {
        return t("typography.addressUnknown");
      }
      if (addressInfo.isNA) {
        return t("typography.na");
      }
      return resolvedAddress || stop.address || "";
    };

    return buildMixedRideTimelineStops(
      mixedRide,
      mixedRideSegments,
      objectRideLocations
    ).map((stop) => ({
      ...stop,
      address: resolveStopAddress(stop)
    }));
  }, [
    showMixedRideSegments,
    mixedRide,
    mixedRideSegments,
    objectRideLocations,
    resolvedAddresses,
    t
  ]);

  const getParsedSpeed = (speed: any) => `${parseFloat(speed.toFixed(2))} km/h`;

  const objectName = objectRide?.object?.name?.trim();

  return (
    <RidesContainer>
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Stack direction={"row"} alignItems={"center"}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIosIcon />
          </IconButton>
          <Stack>
            <Typography variant={"h6"}>
              {objectRide
                ? ongoing
                  ? t("common.ongoingRide")
                  : // @ts-ignore
                    `${t("common.ride")} № ${objectRide?.id}`
                : "Ride not found"}
            </Typography>
            {objectName ? (
              <Typography
                variant={"body1"}
                color={"text.secondary"}
                noWrap
                title={objectName}
                paddingLeft={0.25}
                paddingBottom={1}
              >
                {objectName}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
        {objectRide && !ongoing && (
          <Stack direction={"row"} alignItems={"center"}>
            <Tooltip title={t("rideHistory.tooltip")}>
              <IconButton
                aria-label={t("rideHistory.tooltip")}
                data-testid="ride-history-button"
                onClick={rideHistory.openHistory}
              >
                <HistoryIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={() => setOpenUpdateRide(true)}>
              <EditIcon />
            </IconButton>
          </Stack>
        )}
      </Stack>
      <hr />
      {objectRide && (
        <Box mx={1}>
          <Stack mt={2} direction={"row"} justifyContent={"space-between"}>
            <Stack spacing={2}>
              <Stack spacing={2} direction={"row"} alignItems={"center"}>
                <IconBox>
                  <LocationIcon />
                </IconBox>
                <Typography fontSize={16} variant={"h6"}>
                  {ongoing
                    ? `${utils.formatDate({ date: objectRide?.startDate, dateFormat: "dd MMM yyyy" })}, ${utils.formatDate(
                        {
                          date: objectRide?.startDate,
                          dateFormat: "HH:mm"
                        }
                      )}`
                    : `${utils.formatDate({ date: objectRide?.startDate, dateFormat: "dd MMM yyyy" })}, ${utils.formatDate(
                        {
                          date: objectRide?.startDate,
                          dateFormat: "HH:mm"
                        }
                      )} - ${utils.formatDate({
                        date: objectRide?.endDate,
                        dateFormat: "HH:mm"
                      })}`}
                </Typography>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              {objectRide && (
                <Badge
                  colorScheme={
                    objectRide.status === 1
                      ? "purple"
                      : OBJECT_RIDE_MODES_COLOR[objectRide?.rideMode] || "error"
                  }
                >
                  <div>
                    {objectRide.status === 1
                      ? t("typography.rideInProgress")
                      : OBJECT_RIDE_MODES(t)[objectRide?.rideMode]}
                  </div>
                </Badge>
              )}
              {isManualRide(objectRide) ? (
                <Box
                  sx={{
                    backgroundColor: "#323946",
                    border: "1px solid #76828F",
                    borderRadius: "4px",
                    padding: "4px 8px",
                    color: "#FFFFFF",
                    fontSize: "11px",
                    fontWeight: 600
                  }}
                >
                  {t("common.manualRide")}
                </Box>
              ) : null}
            </Stack>
          </Stack>
          {showMixedRideSegments ? (
            <>
              <MixedRideStackedBar
                segments={mixedRideSegments}
                fallbackPrivateMileage={objectRide?.privateMileage}
              />
              <Stack mt={3}>
                <MixedRideTimeline
                  stops={mixedRideStops}
                  selectedSegmentId={selectedSegmentId}
                  onSegmentSelect={onSegmentSelect}
                />
              </Stack>
            </>
          ) : (
            <Stack mt={3}>
              <LocationTimeline timeLineItems={timeLineItems} />
            </Stack>
          )}
          <Stack
            spacing={2}
            mt={showMixedRideSegments ? 3 : 0}
            pt={showMixedRideSegments ? 3 : 0}
            borderTop={showMixedRideSegments ? "1px solid #D5D9E8" : "none"}
            data-testid="mixed-ride-stats-divider"
          >
            {/* First Row: Max | Duration */}
            {!ongoing && (
              <Stack
                spacing={2}
                direction={"row"}
                justifyContent={"space-between"}
              >
                {!hideSpeedAndDetails && hasMaxSpeed ? (
                  <Stack
                    flex={1}
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                  >
                    <Typography variant={"subtitle2"}>
                      {t("typography.max")}:
                    </Typography>
                    <Badge
                      colorScheme={"error"}
                      icon={<SpeedIconRed />}
                      textVariant={"small"}
                    >
                      {round(objectRide?.maxSpeed || 0, 1)} {"km/h"}
                    </Badge>
                  </Stack>
                ) : null}
                <Stack
                  flex={1}
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                >
                  <Typography variant={"subtitle2"}>
                    {t("typography.duration")}:
                  </Typography>
                  <Badge
                    colorScheme={"info"}
                    icon={<Clock />}
                    textVariant={"small"}
                  >
                    {objectRide
                      ? getObjectRideTotalTimeInMinutes(objectRide)
                      : "0"}{" "}
                    {"min"}
                  </Badge>
                </Stack>
              </Stack>
            )}

            {/* Second Row: Average | Distance */}
            <Stack
              spacing={2}
              direction={"row"}
              justifyContent={"space-between"}
            >
              {!ongoing && !hideSpeedAndDetails && hasAverageSpeed && (
                <Stack
                  flex={1}
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                >
                  <Typography variant={"subtitle2"}>
                    {t("typography.average")}:
                  </Typography>
                  <Badge
                    colorScheme={"info"}
                    icon={<SpeedIcon />}
                    textVariant={"small"}
                  >
                    {round(objectRide?.averageSpeed || 0, 1)} {"km/h"}
                  </Badge>
                </Stack>
              )}
              <Stack
                flex={1}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Typography variant={"subtitle2"}>
                  {t("typography.distance")}:
                </Typography>
                <Badge
                  colorScheme={"info"}
                  icon={<MileageIcon />}
                  textVariant={"small"}
                >
                  {round(objectRide?.mileage || 0, 2)} {"km"}
                </Badge>
              </Stack>
            </Stack>

            {/* Third Row: Deviant | Private (conditional) */}
            {((objectRide as any)?.detourMileage &&
              (objectRide as any).detourMileage > 0) ||
            (objectRide?.privateMileage && objectRide.privateMileage > 0) ? (
              <Stack
                spacing={2}
                direction={"row"}
                justifyContent={"space-between"}
              >
                {/* Left side: Deviant */}
                {(objectRide as any)?.detourMileage &&
                (objectRide as any).detourMileage > 0 ? (
                  <Stack
                    flex={1}
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                  >
                    <Typography variant={"subtitle2"}>
                      {t("typography.detourMileage")}:
                    </Typography>
                    <Badge
                      colorScheme={"info"}
                      icon={<DetourIcon />}
                      textVariant={"small"}
                    >
                      {round((objectRide as any).detourMileage, 2)} {"km"}
                    </Badge>
                  </Stack>
                ) : (
                  <Box flex={1} />
                )}

                {/* Right side: Private */}
                {objectRide?.privateMileage && objectRide.privateMileage > 0 ? (
                  <Stack
                    flex={1}
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                  >
                    <Typography variant={"subtitle2"}>
                      {t("typography.privateMileage")}:
                    </Typography>
                    <Badge
                      colorScheme={"info"}
                      icon={<PrivateMileageIcon />}
                      textVariant={"small"}
                    >
                      {round(objectRide.privateMileage, 2)} {"km"}
                    </Badge>
                  </Stack>
                ) : (
                  <Box flex={1} />
                )}
              </Stack>
            ) : null}
          </Stack>
          {/* @ts-ignore */}
          {objectRide.isMigrated ? (
            <RideBox mt={6} px={2}>
              <Warning style={{ color: "#E28E10" }} />
              <RideMessage ml={2}>{t("typography.migratedRide")}</RideMessage>
            </RideBox>
          ) : hideSpeedAndDetails ? null : (
            <Box mt={5} mr={2}>
              <KeyValueTable title={t("typography.details")} records={{}} />
              {objectRideLocations?.map((item, index) => (
                <RecordsWrapper
                  onClick={() => onLocationItemClick(item)}
                  key={`${index}`}
                >
                  <RecordKey>
                    {DateTime.fromISO(item?.date).toFormat(
                      "dd MMM yyyy, HH:mm"
                    )}
                  </RecordKey>
                  {/* @ts-ignore */}
                  <RecordValue>{getParsedSpeed(item?.speed)}</RecordValue>
                </RecordsWrapper>
              ))}
            </Box>
          )}
        </Box>
      )}
      {objectRide && (
        <UpdateObjectRideForm
          open={openUpdateRide}
          onClose={() => setOpenUpdateRide(false)}
          objectRide={objectRide}
          objectId={resolvedObjectId}
        />
      )}
      {objectRide && !ongoing && (
        <RideHistoryModal
          open={rideHistory.open}
          onClose={rideHistory.closeHistory}
          items={rideHistory.items}
          loading={rideHistory.loading}
          rideId={(objectRide as types.IObjectRide & { id?: number }).id}
        />
      )}
    </RidesContainer>
  );
};

export default ObjectRideDetailsDrawer;
